"use client";

import { formatBalance } from "@cede/utils";
import { Alert } from "@/components/features/notifications/Alerts/Alert";
import { TokenIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useMerklRewards } from "@/hooks/campaign/useMerklRewards";
import { AlertTriangle, Info } from "lucide-react";
import { useSnackbar } from "notistack";
import { BaseError } from "viem";

const merklClaimButtonLabel = ({
  hasClaim,
  isAwaitingMerklIndex,
}: {
  hasClaim: boolean;
  isAwaitingMerklIndex: boolean;
}) => {
  if (hasClaim) return "Claim via Merkl";
  if (isAwaitingMerklIndex) return "Claimed, Merkl is updating";
  return "Nothing to claim yet";
};

export const MerklRewardsSection = () => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    isEnabled,
    isLoading,
    isError,
    campaigns,
    claimToken,
    totalClaimable,
    walletClaimableAmount,
    claimTransaction,
    isAwaitingMerklIndex,
    isImpersonating,
    claim,
    isClaiming,
  } = useMerklRewards({
    onClaimSuccess: () => enqueueSnackbar("Merkl rewards claimed successfully", { variant: "success" }),
    onClaimError: (error) =>
      enqueueSnackbar(error instanceof BaseError ? error.shortMessage : error.message, { variant: "error" }),
  });

  if (!isEnabled || isLoading) return null;

  if (isError) {
    return (
      <Alert
        variant="warning"
        icon={<AlertTriangle className="h-5 w-5" />}
        content="Merkl rewards are temporarily unavailable, try again later."
      />
    );
  }

  if (campaigns.length === 0) return null;

  return (
    <div className="rounded-2xl bg-cede-bg-component/50 border border-cede-border-container p-5">
      <h3 className="text-lg font-semibold text-cede-text-primary mb-3">Paid via Merkl</h3>
      <div className="space-y-2">
        {campaigns.map((campaign) => (
          <div
            key={campaign.campaignId}
            className="rounded-lg border border-cede-border-container bg-cede-bg-component p-3 space-y-1"
          >
            <p className="text-base font-medium text-cede-text-primary">{campaign.campaignTitle}</p>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-cede-text-secondary">Claimable</span>
              <span className="text-sm text-cede-text-primary flex items-center gap-1">
                <TokenIcon symbol={campaign.rewardTokenSymbol} size={14} />
                {formatBalance(campaign.claimable, { roundForFiat: true })}
                <span className="text-cede-text-secondary">{campaign.rewardTokenSymbol}</span>
              </span>
            </div>
            {campaign.claimableSoon.gt(0) && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-cede-text-secondary flex items-center gap-1">
                  <TokenIcon symbol={campaign.rewardTokenSymbol} size={14} />
                  {formatBalance(campaign.claimableSoon, { roundForFiat: true })} {campaign.rewardTokenSymbol}
                </span>
                <p className="body2 text-right text-cede-primary">Available within ~12h</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {claimTransaction && claimToken && (
        <Alert
          variant="info"
          icon={<Info className="h-5 w-5" />}
          className="mt-3"
          content={`Claiming through Merkl also pays out any other rewards this wallet holds on Merkl in ${claimToken.symbol}.${
            walletClaimableAmount?.gt(totalClaimable)
              ? ` You will receive ${formatBalance(walletClaimableAmount, { roundForFiat: true })} ${claimToken.symbol} in total.`
              : ""
          }`}
        />
      )}
      <Button
        variant="secondary"
        size="medium"
        className="mt-3 w-full"
        loading={isClaiming}
        disabled={!claimTransaction || isImpersonating}
        onClick={() => claimTransaction && claim(claimTransaction)}
      >
        {merklClaimButtonLabel({ hasClaim: claimTransaction != null, isAwaitingMerklIndex })}
      </Button>
    </div>
  );
};
